import React, { useMemo } from 'react';
import { CreditCard, X, TrendingDown, ArrowUpRight, TrendingUp } from 'lucide-react';
import { motion } from 'motion/react';
import { Account } from '../types';
import { getInstitutionById } from '../constants/institutions';

interface CreditCardReportProps {
  accounts: Account[];
  onClose: () => void;
}

export const CreditCardReport: React.FC<CreditCardReportProps> = ({ accounts, onClose }) => {
  const creditCards = useMemo(() => {
    return accounts.filter(acc => acc.type === 'TARJETA DE CREDITO' && acc.isActive !== false);
  }, [accounts]);

  const stats = useMemo(() => {
    const totalLine = creditCards.reduce((acc, curr) => acc + (curr.creditLimit || 0), 0);
    const totalAvailable = creditCards.reduce((acc, curr) => acc + curr.balance, 0);
    const totalUsed = totalLine - totalAvailable;

    return { totalLine, totalAvailable, totalUsed };
  }, [creditCards]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  const getPercentage = (used: number, limit: number) => {
    if (limit === 0) return 0;
    return Math.min(100, Math.max(0, (used / limit) * 100));
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex flex-col">
      <div className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 bg-zinc-900 border border-zinc-800 p-4 md:p-6 rounded-3xl">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-500/10 rounded-2xl border border-amber-500/20">
                <CreditCard className="text-amber-500" size={24} />
              </div>
              <div>
                <h1 className="text-2xl font-black text-white">Informe de Tarjetas de Crédito</h1>
                <p className="text-sm text-zinc-500 mt-1">Línea de crédito, límite utilizado y saldo disponible.</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-3 bg-zinc-800 hover:bg-zinc-700 rounded-xl transition-all text-zinc-400 hover:text-white"
            >
              <X size={24} />
            </button>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-zinc-900 border border-zinc-800 p-6 rounded-3xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-indigo-500/10 rounded-xl border border-indigo-500/20">
                  <CreditCard className="text-indigo-500" size={20} />
                </div>
                <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Total Línea de Crédito</span>
              </div>
              <div className="text-3xl font-black text-white">{formatCurrency(stats.totalLine)}</div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.1 } }} className="bg-zinc-900 border border-zinc-800 p-6 rounded-3xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-500/10 rounded-xl border border-red-500/20">
                  <TrendingDown className="text-red-500" size={20} />
                </div>
                <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Importe Utilizado (Deuda)</span>
              </div>
              <div className="text-3xl font-black text-white">{formatCurrency(stats.totalUsed)}</div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.2 } }} className="bg-zinc-900 border border-zinc-800 p-6 rounded-3xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                  <ArrowUpRight className="text-emerald-500" size={20} />
                </div>
                <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Saldo Disponible</span>
              </div>
              <div className="text-3xl font-black text-white">{formatCurrency(stats.totalAvailable)}</div>
            </motion.div>
          </div>

          {/* Details Table */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.3 } }} className="bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-zinc-800 bg-zinc-800/20">
              <h3 className="font-bold text-white text-lg">Detalle por Tarjeta</h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-zinc-800 text-xs text-zinc-500">
                    <th className="p-4 font-bold uppercase tracking-wider">Tarjeta / Banco</th>
                    <th className="p-4 font-bold uppercase tracking-wider text-right">Línea de Crédito</th>
                    <th className="p-4 font-bold uppercase tracking-wider text-right">Importe Utilizado</th>
                    <th className="p-4 font-bold uppercase tracking-wider text-right">Saldo Disponible</th>
                    <th className="p-4 font-bold uppercase tracking-wider text-center">Uso</th>
                  </tr>
                </thead>
                <tbody>
                  {creditCards.map(acc => {
                    const line = acc.creditLimit || 0;
                    const available = acc.balance;
                    const used = line - available;
                    const percentage = getPercentage(used, line);
                    const inst = acc.institutionId ? getInstitutionById(acc.institutionId) : null;
                    
                    return (
                      <tr key={acc.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center font-bold text-zinc-500 text-xs uppercase">
                              {inst ? inst.name.substring(0, 2) : 'TC'}
                            </div>
                            <div>
                              <div className="text-sm font-bold text-white leading-tight">{acc.name}</div>
                              <div className="text-xs text-zinc-500 mt-0.5">{inst ? inst.name : 'Tarjeta de Crédito'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 font-mono text-sm text-right text-indigo-400 font-medium">
                          {formatCurrency(line)}
                        </td>
                        <td className="p-4 font-mono text-sm text-right text-red-400 font-medium">
                          {formatCurrency(used)}
                        </td>
                        <td className="p-4 font-mono text-sm text-right text-emerald-400 font-medium">
                          {formatCurrency(available)}
                        </td>
                        <td className="p-4">
                          <div className="flex flex-col items-center gap-1.5">
                            <div className="w-24 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${percentage > 85 ? 'bg-red-500' : percentage > 60 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-[10px] text-zinc-500 font-bold">{percentage.toFixed(1)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  
                  {creditCards.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-12 text-center text-zinc-500">
                        No hay tarjetas de crédito registradas.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
